import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import type { AccountManager, ActivityDto, CFGLEntry, DocumentFiling, DocumentFilingDto, Filer } from 'admin/src/types';

export type OverviewDataDto = {
  awaitingApprovalList: AccountManager[];
  latestSubmissionsList: DocumentFilingDto[];
};

export type OverviewData = {
  awaitingApprovalList: OverviewDataDto['awaitingApprovalList'];
  latestSubmissionsList: DocumentFiling[];
};

export type CampaignFinanceAppState = {
  isLoadingGlEntries: boolean;
  glEntries: CFGLEntry[];
  isLoading: boolean;
  isLoadingOverview: boolean;
  isLoadingCfActivityList: boolean;
  cfActivityList: ActivityDto[];
  allFilers: Filer[];
  overviewData: OverviewDataDto;
  filerFilings: Record<string, { isLoading: boolean; filings: DocumentFilingDto[] }>;
};

const initialState: CampaignFinanceAppState = {
  isLoadingGlEntries: false,
  glEntries: [],
  isLoading: false,
  isLoadingOverview: false,
  isLoadingCfActivityList: false,
  cfActivityList: [],
  allFilers: [],
  overviewData: {
    awaitingApprovalList: [],
    latestSubmissionsList: [],
  },
  filerFilings: {},
};

const campaignFinanceAppState = createSlice({
  name: 'campaignFinanceApp',
  initialState,
  reducers: {
    setLoadingCfGlEntries: (state, action: PayloadAction<boolean>) => {
      state.isLoadingGlEntries = action.payload;
    },
    setCfGlEntries: (state, action: PayloadAction<CFGLEntry[]>) => {
      state.glEntries = action.payload;
    },
    loadCfGlEntries: () => {},
    loadAllFilers: (state) => {
      state.isLoading = true;
    },
    setAllFilers: (state, action: PayloadAction<Filer[]>) => {
      state.allFilers = action.payload;
    },
    setLoadingAllFilers: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    loadOverviewData: (state) => {
      state.isLoadingOverview = true;
    },
    setLoadingOverview: (state, action: PayloadAction<boolean>) => {
      state.isLoadingOverview = action.payload;
    },
    setOverviewData: (state, action: PayloadAction<OverviewDataDto>) => {
      state.overviewData = action.payload;
    },
    loadCfActivityList: () => {},
    setLoadingCfActivityList: (state, action: PayloadAction<boolean>) => {
      state.isLoadingCfActivityList = action.payload;
    },
    setCfActivityList: (state, action: PayloadAction<ActivityDto[]>) => {
      state.cfActivityList = action.payload;
    },
    loadFilerFilings: (state, action) => {},
    setFilerFilings: (
      state,
      action: PayloadAction<{
        filerId: string;
        filings: { filing: DocumentFilingDto }[];
      }>,
    ) => {
      const { filerId, filings } = action.payload;

      state.filerFilings[filerId] = state.filerFilings[filerId] || {
        isLoading: false,
        filings: [],
      };
      state.filerFilings[filerId].filings = filings.map(({ filing }: { filing: DocumentFilingDto }) => filing);
      state.filerFilings[filerId].isLoading = false;
    },
    setLoadingFilerFilings: (state, action: PayloadAction<{ filerId: string; isLoading: boolean }>) => {
      const { filerId, isLoading } = action.payload;

      state.filerFilings[filerId] = state.filerFilings[filerId] || {
        isLoading: false,
        filings: [],
      };
      state.filerFilings[filerId].isLoading = isLoading;
    },
  },
});

export const {
  setAllFilers,
  loadAllFilers,
  setLoadingAllFilers,
  loadOverviewData,
  setLoadingOverview,
  setOverviewData,
  loadFilerFilings,
  setFilerFilings,
  setLoadingFilerFilings,
  setLoadingCfGlEntries,
  setCfGlEntries,
  loadCfGlEntries,
  loadCfActivityList,
  setLoadingCfActivityList,
  setCfActivityList,
} = campaignFinanceAppState.actions;

export default campaignFinanceAppState.reducer;
